import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LogOut, Trash2, Plus, Inbox, Image, Quote, Settings as Cog, Phone, Mail, Calendar } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState("");

  const loadAll = async () => {
    const [inq, port, test, set] = await Promise.allSettled([
      api.get("/admin/inquiries"),
      api.get("/portfolio"),
      api.get("/testimonials"),
      api.get("/admin/settings"),
    ]);
    if (inq.status === "fulfilled") setInquiries(inq.value.data);
    if (port.status === "fulfilled") setPortfolio(port.value.data);
    if (test.status === "fulfilled") setTestimonials(test.value.data);
    if (set.status === "fulfilled") setOwnerEmail(set.value.data.owner_email || "");
  };

  useEffect(() => {
    if (!user) checkAuth();
    loadAll();
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const newCount = inquiries.filter((i) => i.status === "new").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-heirloom-bone">
      <header className="border-b border-heirloom-line bg-heirloom-bone sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="font-display text-xl text-heirloom-obsidian">Arihant<span className="text-heirloom-crimson">.</span> <span className="overline text-heirloom-obsidian/40 ml-2">Studio</span></div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-body text-sm text-heirloom-obsidian/60">{user?.email}</span>
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={doLogout} className="rounded-full border-heirloom-line">
              <LogOut size={15} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
          <Stat label="New Inquiries" value={newCount} icon={<Inbox size={18} />} accent />
          <Stat label="Total Inquiries" value={inquiries.length} icon={<Inbox size={18} />} />
          <Stat label="Portfolio Items" value={portfolio.length} icon={<Image size={18} />} />
          <Stat label="Testimonials" value={testimonials.length} icon={<Quote size={18} />} />
        </div>

        <Tabs defaultValue="inquiries">
          <TabsList className="bg-heirloom-line/40 rounded-lg">
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="inquiries" className="mt-6">
            <InquiriesPanel inquiries={inquiries} reload={loadAll} />
          </TabsContent>
          <TabsContent value="portfolio" className="mt-6">
            <PortfolioPanel items={portfolio} reload={loadAll} />
          </TabsContent>
          <TabsContent value="testimonials" className="mt-6">
            <TestimonialsPanel items={testimonials} reload={loadAll} />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsPanel ownerEmail={ownerEmail} setOwnerEmail={setOwnerEmail} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const Stat = ({ label, value, icon, accent }) => (
  <div className={`border border-heirloom-line p-5 ${accent ? "bg-heirloom-crimson text-heirloom-bone" : "bg-white"}`}>
    <div className="flex items-center justify-between">
      <span className={`overline ${accent ? "text-heirloom-bone/70" : "text-heirloom-obsidian/40"}`}>{label}</span>
      <span className={accent ? "text-heirloom-bone/70" : "text-heirloom-crimson"}>{icon}</span>
    </div>
    <div className="font-display text-4xl mt-3">{value}</div>
  </div>
);

function InquiriesPanel({ inquiries, reload }) {
  const del = async (id) => {
    await api.delete(`/admin/inquiries/${id}`);
    toast.success("Inquiry deleted");
    reload();
  };
  const mark = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status });
    reload();
  };
  if (!inquiries.length)
    return <Empty text="No inquiries yet. New event inquiries will appear here." />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {inquiries.map((inq) => (
        <div key={inq.id} data-testid={`inquiry-card-${inq.id}`} className="border border-heirloom-line bg-white p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="font-display text-2xl text-heirloom-obsidian">{inq.name}</h3>
                <span className={`overline px-2 py-0.5 rounded-full text-[0.6rem] ${inq.status === "new" ? "bg-heirloom-crimson text-heirloom-bone" : "bg-heirloom-line text-heirloom-obsidian/60"}`}>{inq.status}</span>
              </div>
              <span className="overline text-heirloom-crimson">{inq.event_type}</span>
            </div>
            <button data-testid={`inquiry-delete-${inq.id}`} onClick={() => del(inq.id)} className="text-heirloom-obsidian/40 hover:text-heirloom-crimson transition-colors">
              <Trash2 size={17} />
            </button>
          </div>
          <div className="mt-4 space-y-2 font-body text-sm text-heirloom-obsidian/70">
            <div className="flex items-center gap-2"><Phone size={14} className="text-heirloom-crimson" /> {inq.phone}</div>
            {inq.email && <div className="flex items-center gap-2"><Mail size={14} className="text-heirloom-crimson" /> {inq.email}</div>}
            {inq.event_date && <div className="flex items-center gap-2"><Calendar size={14} className="text-heirloom-crimson" /> {inq.event_date}</div>}
            {inq.budget && <div className="pl-6">Budget: {inq.budget}</div>}
            {inq.message && <p className="pt-2 italic text-heirloom-obsidian/60">&ldquo;{inq.message}&rdquo;</p>}
          </div>
          {inq.status === "new" && (
            <Button data-testid={`inquiry-mark-${inq.id}`} size="sm" variant="outline" className="mt-4 rounded-full border-heirloom-line" onClick={() => mark(inq.id, "contacted")}>
              Mark as contacted
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function PortfolioPanel({ items, reload }) {
  const [form, setForm] = useState({ title: "", category: "Wedding", image: "", span: "small" });
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); return; }
    const reader = new FileReader();
    reader.onloadend = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const add = async () => {
    if (!form.title || !form.image) { toast.error("Add a title and image"); return; }
    setSaving(true);
    try {
      await api.post("/admin/portfolio", form);
      toast.success("Portfolio item added");
      setForm({ title: "", category: "Wedding", image: "", span: "small" });
      reload();
    } catch { toast.error("Failed to add"); }
    finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/admin/portfolio/${id}`); toast.success("Deleted"); reload(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-1 border border-heirloom-line bg-white p-6 h-fit">
        <h3 className="font-display text-2xl text-heirloom-obsidian mb-5">Add Item</h3>
        <div className="space-y-4">
          <div><Label className="overline">Title</Label><Input data-testid="portfolio-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-heirloom-line" /></div>
          <div><Label className="overline">Category</Label><Input data-testid="portfolio-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none border-heirloom-line" /></div>
          <div>
            <Label className="overline">Layout Size</Label>
            <select data-testid="portfolio-span-input" value={form.span} onChange={(e) => setForm({ ...form, span: e.target.value })} className="w-full border border-heirloom-line px-3 py-2 mt-1 bg-white text-sm">
              <option value="small">Small</option><option value="wide">Wide</option><option value="tall">Tall</option>
            </select>
          </div>
          <div><Label className="overline">Image</Label><input data-testid="portfolio-image-input" type="file" accept="image/*" onChange={onFile} className="block w-full text-sm mt-1 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-heirloom-obsidian file:text-heirloom-bone file:rounded-full" /></div>
          {form.image && <img src={form.image} alt="preview" className="w-full h-32 object-cover border border-heirloom-line" />}
          <Button data-testid="portfolio-add-btn" onClick={add} disabled={saving} className="w-full rounded-full bg-heirloom-crimson hover:bg-heirloom-crimson/90"><Plus size={15} className="mr-2" />{saving ? "Adding…" : "Add Item"}</Button>
        </div>
      </div>
      <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4 content-start">
        {items.map((it) => (
          <div key={it.id} data-testid={`portfolio-admin-${it.id}`} className="relative group border border-heirloom-line overflow-hidden aspect-square">
            <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-heirloom-obsidian/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
              <span className="font-display text-heirloom-bone">{it.title}</span>
              <button data-testid={`portfolio-delete-${it.id}`} onClick={() => del(it.id)} className="text-heirloom-bone bg-heirloom-crimson rounded-full p-2"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsPanel({ items, reload }) {
  const [form, setForm] = useState({ name: "", event: "", quote: "" });
  const add = async () => {
    if (!form.name || !form.quote) { toast.error("Name and quote required"); return; }
    await api.post("/admin/testimonials", form);
    toast.success("Testimonial added"); setForm({ name: "", event: "", quote: "" }); reload();
  };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); reload(); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-1 border border-heirloom-line bg-white p-6 h-fit">
        <h3 className="font-display text-2xl text-heirloom-obsidian mb-5">Add Testimonial</h3>
        <div className="space-y-4">
          <div><Label className="overline">Client Name</Label><Input data-testid="testimonial-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-heirloom-line" /></div>
          <div><Label className="overline">Event</Label><Input data-testid="testimonial-event-input" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} className="rounded-none border-heirloom-line" /></div>
          <div><Label className="overline">Quote</Label><Textarea data-testid="testimonial-quote-input" rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="rounded-none border-heirloom-line" /></div>
          <Button data-testid="testimonial-add-btn" onClick={add} className="w-full rounded-full bg-heirloom-crimson hover:bg-heirloom-crimson/90"><Plus size={15} className="mr-2" />Add</Button>
        </div>
      </div>
      <div className="lg:col-span-2 space-y-4">
        {items.map((t) => (
          <div key={t.id} data-testid={`testimonial-admin-${t.id}`} className="border border-heirloom-line bg-white p-5 flex justify-between gap-4">
            <div>
              <p className="font-display text-lg italic text-heirloom-obsidian">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-2"><span className="font-body font-bold text-sm">{t.name}</span> <span className="overline text-heirloom-crimson ml-2">{t.event}</span></div>
            </div>
            <button data-testid={`testimonial-delete-${t.id}`} onClick={() => del(t.id)} className="text-heirloom-obsidian/40 hover:text-heirloom-crimson shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPanel({ ownerEmail, setOwnerEmail }) {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { owner_email: ownerEmail });
      toast.success("Settings saved. Inquiry emails will go here.");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-lg border border-heirloom-line bg-white p-8">
      <div className="flex items-center gap-2 mb-2"><Cog size={18} className="text-heirloom-crimson" /><h3 className="font-display text-2xl text-heirloom-obsidian">Notification Settings</h3></div>
      <p className="font-body text-sm text-heirloom-obsidian/60 mb-6">New event inquiries will be emailed to this address.</p>
      <Label className="overline">Owner Email</Label>
      <Input data-testid="settings-email-input" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} className="rounded-none border-heirloom-line mt-1" placeholder="owner@arihantevents.com" />
      <Button data-testid="settings-save-btn" onClick={save} disabled={saving} className="mt-6 rounded-full bg-heirloom-crimson hover:bg-heirloom-crimson/90">{saving ? "Saving…" : "Save Settings"}</Button>
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="border border-dashed border-heirloom-line py-20 text-center">
    <p className="font-body text-heirloom-obsidian/50">{text}</p>
  </div>
);
